import { type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { BriefcaseBusiness, Clock3, MapPin, UserRound } from 'lucide-react'
import { useLang } from '../../contexts/LangContext'

export default function ProviderSettingsLayout({
  title,
  body,
  children,
}: {
  title: string
  body: string
  children: ReactNode
}) {
  const { t } = useLang()

  const items = [
    {
      to: '/provider/profile',
      label: t('Προφίλ', 'Profile'),
      body: t('Βιογραφικό, γλώσσες, τιμή και avatar.', 'Bio, languages, rate, and avatar.'),
      icon: <UserRound className="h-4 w-4" />,
    },
    {
      to: '/provider/services',
      label: t('Υπηρεσίες', 'Services'),
      body: t('Τι προσφέρεις και με ποιες προσαρμογές.', 'What you offer and any custom settings.'),
      icon: <BriefcaseBusiness className="h-4 w-4" />,
    },
    {
      to: '/provider/availability',
      label: t('Διαθεσιμότητα', 'Availability'),
      body: t('Εβδομαδιαίο ωράριο και ημερολογιακές εξαιρέσεις.', 'Weekly schedule and date-specific exceptions.'),
      icon: <Clock3 className="h-4 w-4" />,
    },
    {
      to: '/provider/areas',
      label: t('Περιοχές', 'Areas'),
      body: t('Ταχυδρομικοί κώδικες και ονομασίες περιοχών.', 'Postal codes and area labels.'),
      icon: <MapPin className="h-4 w-4" />,
    },
  ]

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">
        {t('Ρυθμίσεις επαγγελματία', 'Provider settings')}
      </p>
      <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">{title}</h1>
      <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">{body}</p>

      <div className="mt-8 grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="space-y-3 lg:sticky lg:top-24 lg:self-start">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => [
                'block rounded-[1.5rem] border p-4 transition',
                isActive
                  ? 'border-primary-200 bg-primary-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-primary-200 hover:bg-slate-50',
              ].join(' ')}
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white text-primary-700 shadow-sm">
                  {item.icon}
                </span>
                <div>
                  <p className="font-semibold text-slate-950">{item.label}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-500">{item.body}</p>
                </div>
              </div>
            </NavLink>
          ))}
        </aside>

        <main>{children}</main>
      </div>
    </div>
  )
}
